import '@Styles/theme.scss'
import '@Styles/global.scss'

import { ApolloProvider } from '@apollo/client'
import keyBy from 'lodash/keyBy'
import mapValues from 'lodash/mapValues'
import noop from 'lodash/noop'
import App, { AppContext, AppInitialProps, AppProps } from 'next/app'
import { Brygada_1918 } from 'next/font/google'
import Head from 'next/head'
import { appWithTranslation } from 'next-i18next/pages'
import { NuqsAdapter } from 'nuqs/adapters/next/pages'
import React, { useMemo } from 'react'

import { useAnalyticsInit } from '@Analytics/init'
import { SideCartProvider } from '@Domains/Cart/Context/SideCartContextProvider'
import { DiscountCodesProvider } from '@Domains/Cart/Feature/DiscountCodes'
import { CartExtendedCoupons } from '@Domains/Cart/Feature/DiscountCodes/ExtendedCoupons'
import { ClubProvider } from '@Domains/Club/Provider'
import { GlobalNavigationQuery } from '@Generated/graphql'
import { initializeApollo } from '@Globals/Apollo'
import { CMSModalProvider } from '@Globals/CMS/Modal/Provider'
import { CMSModalRenderer } from '@Globals/CMS/Modal/Renderer'
import { ClientConfig, getClientConfig, setClientConfig } from '@Globals/Config'
import { ConfigProvider } from '@Globals/Config/Provider'
import emptyStore from '@Globals/emptyStore'
import { selectFont } from '@Globals/Fonts'
import { DataLayer, DataLayerInit } from '@Globals/Google/DataLayer'
import { GTagScript } from '@Globals/Google/GTag'
import i18nConfig from '@Globals/Locales/i18n'
import { AvailableLocale, Bar, CMSData, FacetDescriptions, MetadataProvider, PaymentMethod, Store } from '@Globals/Metadata'
import { addSurrogateKey as addSurrogateKeyHeader, addVary } from '@Globals/Page/Headers'
import { getRequestContext, hydrateRequest, releaseRequestContext } from '@Globals/RequestContext'
import { PreloadRemover } from '@Globals/Scripts/PreloadRemover'
import { ScrollPositionProvider } from '@Globals/ScrollPosition'
import { processURLSearchParams } from '@Globals/SearchParams'
import SiftInit from '@Globals/Sift'
import { SplitTestsProvider } from '@Globals/SplitTests/Context'
import { Experiment } from '@Globals/SplitTests/Experiment'
import { Group, SplitTests, Test } from '@Globals/SplitTests/types'
import { UserCentricsFallback, UserCentricsScript } from '@Globals/Usercentrics/script'

const brygada = Brygada_1918({
  display: 'swap',
  preload: false,
  subsets: ['latin'],
  variable: '--font-brygada',
})

interface MyAppProps extends AppProps<any> {
  activateSplitTest: (test: string, group: Group) => void
  addSurrogateKey: (key: string) => void
  availableLocales: AvailableLocale[]
  bars: Bar[]
  clientConfig: ClientConfig
  cmsData: CMSData
  facetDescriptions: FacetDescriptions
  navigation: GlobalNavigationQuery['navigation']
  navigationTop: GlobalNavigationQuery['navigationTop']
  overrideStoreURLFull: string | false
  paymentMethods: PaymentMethod[]
  splitTests: SplitTests
  store: Store
}

interface MyAppInitialProps extends AppInitialProps {
  activateSplitTest: (test: string, group: Group) => void
  addSurrogateKey: (key: string) => void
  availableLocales: AvailableLocale[]
  bars: Bar[]
  clientConfig: ClientConfig
  cmsData: CMSData
  facetDescriptions: FacetDescriptions
  navigation: GlobalNavigationQuery['navigation']
  navigationTop: GlobalNavigationQuery['navigationTop']
  overrideStoreURLFull: string | false
  paymentMethods: PaymentMethod[]
  splitTests: SplitTests
  store: Store
}

const MyApp = (
  {
    Component,
    activateSplitTest,
    addSurrogateKey,
    availableLocales,
    bars,
    clientConfig,
    cmsData,
    facetDescriptions,
    navigation,
    navigationTop,
    overrideStoreURLFull,
    pageProps,
    paymentMethods,
    router,
    splitTests,
    store,
  }: MyAppProps,
) => {
  // Must run before anything reads the config outside React, such as the Apollo client below.
  setClientConfig(clientConfig)

  useAnalyticsInit({
    page: pageProps?.page ?? (router?.route === '/_error'
      ? {
        type: 'error',
        id: '',
      }
      : {}),
    splitTests,
    store,
  })
  const locale = store?.config?.localeCode
  const preview = pageProps.preview || false
  const previewOverrideCurrentDate = pageProps.previewOverrideCurrentDate || undefined
  const apolloClient = useMemo(
    () => initializeApollo(pageProps.initialApolloState || undefined, {
      locale,
      preview,
      previewOverrideCurrentDate,
    }),
    [pageProps.initialApolloState, locale, preview, previewOverrideCurrentDate],
  )

  return (
    <ConfigProvider value={clientConfig}>
      <NuqsAdapter processUrlSearchParams={processURLSearchParams}>
        <SplitTestsProvider
          value={{
            activateSplitTest: activateSplitTest || noop,
            observeSplitTest: (test: Test) => {
              if (typeof window === 'undefined' || !test) {
                return
              }

              const w = (window as any)
              w.th_split_tests_observed = w.th_split_tests_observed || []
              if (w.th_split_tests_observed?.includes?.(test)) {
                return
              }

              w.th_split_tests_observed.push(test)
            },
            splitTests,
          }}
        >
          <Head>
            <meta content="width=device-width, initial-scale=1" name="viewport" />
            <link
              as="font"
              crossOrigin="anonymous"
              href={`/static/fonts/${selectFont(store)}/normal.woff`}
              type="font/woff"
            />
            <link
              as="font"
              crossOrigin="anonymous"
              href={`/static/fonts/${selectFont(store)}/bold.woff`}
              type="font/woff"
            />
            {store?.config?.usercentricsEnabled && (
              <link href="//app.usercentrics.eu" rel="preconnect" />
            )}
            <DataLayerInit />
          </Head>
          <DataLayer
            input={{
              HotjarSiteID: 70023,
              country: store?.config?.countryCode,
              currencyRates: mapValues(keyBy(store.config?.adwordsCurrencyRates, 'key'), 'value'),
              localCurrencyKey: store?.config?.currencyCode,
              storeId: store?.id,
            }}
          />
          <GTagScript
            measurementID={`AW-${store?.config?.adwordsTrackingID}`}
            storeCountryCode={store?.config?.countryCode || ''}
            storeID={store?.id || ''}
          />
          {store?.config?.usercentricsEnabled ? <UserCentricsScript storeID={store.id} /> : <UserCentricsFallback />}
          <MetadataProvider
            addSurrogateKey={addSurrogateKey || noop}
            alternateLinks={pageProps?.alternateLinks || []}
            availableLocales={availableLocales}
            bars={bars}
            cmsData={cmsData}
            facetDescriptions={facetDescriptions}
            fonts={[brygada.variable]}
            globalAnchorInjections={pageProps?.globalAnchorInjections || {}}
            navigation={navigation}
            navigationTop={navigationTop}
            overrideStoreURLFull={overrideStoreURLFull}
            page={pageProps?.page}
            paymentMethods={paymentMethods}
            store={store}
          >
            <Experiment test="campaign_override_v1" />
            <Experiment test="cart_v3" />
            <Experiment test="extended_return_v1" />
            <Experiment test="shipping_threshold_discount_v1" />
            <ApolloProvider client={apolloClient}>
              <ScrollPositionProvider>
                <ClubProvider>
                  <CMSModalProvider>
                    <DiscountCodesProvider>
                      <SideCartProvider>
                        <Component {...pageProps} />
                        <CartExtendedCoupons />
                        <CMSModalRenderer />
                      </SideCartProvider>
                    </DiscountCodesProvider>
                  </CMSModalProvider>
                </ClubProvider>
              </ScrollPositionProvider>
            </ApolloProvider>
          </MetadataProvider>
          <SiftInit />
          <PreloadRemover />
        </SplitTestsProvider>
      </NuqsAdapter>
    </ConfigProvider>
  )
}

MyApp.getInitialProps = async (appContext: AppContext): Promise<MyAppInitialProps> => {
  const appProps = await App.getInitialProps(appContext)

  // Server side: proxy.ts has resolved the store, split tests, preview and cached globals for this request.
  // Hydrating req/res here keeps the req.th* contract getServerSideProps and _document rely on; Next runs
  // App.getInitialProps before any getServerSideProps.
  const { req, res } = appContext.ctx
  const requestContext = getRequestContext(req)
  if (req && res) {
    if (requestContext) {
      hydrateRequest(req, res, requestContext)
      if (requestContext.storeFound) {
        addSurrogateKeyHeader(res, `STO${requestContext.store.id}`)
      }
    }
    res.once('close', () => releaseRequestContext(req))
  }

  const addSurrogateKey = res ? (key: string) => addSurrogateKeyHeader(res, key) : noop
  const bars = appContext?.ctx?.req?.thBars || []
  const facetDescriptions = appContext?.ctx?.req?.thFacetDescriptions || {}
  const availableLocales = appContext?.ctx?.req?.thAvailableLocales || []
  const cmsData: CMSData = appContext?.ctx?.req?.thCMSData || {
    latestCollections: [],
    urls: {
      aboutUs: null,
      articles: null,
      cookiePolicy: null,
      csr: null,
      faq: null,
      giftGuide: null,
      press: null,
      privacyPolicy: null,
      returns: null,
      terms: null,
    },
  }
  const navigation = appContext?.ctx?.req?.thNavigation || []
  const navigationTop = appContext?.ctx?.req?.thNavigationTop || []
  const overrideStoreURLFull = appContext?.ctx?.req?.thOverrideStoreURLFull || false
  const paymentMethods = appContext?.ctx?.req?.thPaymentMethods || []
  const splitTests = appContext?.ctx?.req?.thSplitTests || {}
  const store = appContext?.ctx?.req?.thStore || { ...emptyStore }

  return {
    ...appProps,
    activateSplitTest: (test: string, group: Group) => {
      if (process.env.NODE_ENV === 'development') {
        console.info(`[split-test] activating ${test} ${group}`)
      }

      if (!res) {
        return
      }

      const header = splitTests[test]?.header
      if (!header) {
        if (process.env.NODE_ENV === 'development') {
          console.info(`[split-test] no header defined for ${test} ${group}`)
        }
        return
      }

      splitTests[test].active = true

      addVary(res, header)
    },
    addSurrogateKey,
    availableLocales,
    bars,
    clientConfig: getClientConfig(),
    cmsData,
    facetDescriptions,
    navigation,
    navigationTop,
    overrideStoreURLFull,
    pageProps: (appProps.pageProps || {}),
    paymentMethods,
    splitTests,
    store,
  }
}

// The client side of the next-i18next config: the shared part plus the public locale path (only used to fetch a
// namespace that is not in the hydrated store). serializeConfig is off, so this is the only copy the client sees.
export default appWithTranslation(MyApp, { ...i18nConfig, localePath: '/static/locales' })
